import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import './meal_detail.css';
import {
  Box,
  DialogContent,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIosOutlinedIcon from '@mui/icons-material/ArrowBackIosOutlined';
import { isURL, urlDisplayableHostName } from '../../services/strings';
import EditableLabelAndInput from '../common/editable_label_and_input';
import ServingsInput from './servings_input';
import MealTypesInput from './meal_types_input';
import PreparationList from './preparation_list';
import { SMORG_NUTRITION_ADDON } from '../../services/addons';
import imgPlaceholder from '../../assets/images/imgPlaceholder.png';
import TagsInput from './tags_input';
import MealDetailMobileActionsMenu from './meal_detail_mobile_actions_menu';
import { deduplicate } from '../../services/arrays';
import EditMealImagePopupMenu from './edit_meal_image_popup_menu';
import IngredientListMobile from './ingredient_list_mobile';
import {
  useGetGlobalTagsQuery,
  useUpdateUserProfileMutation,
} from '../../app/smorgAppSyncGraphQLApi';
import { userIsCreator } from '../../services/users';
import { showMealDetailGuide } from '../../operations/product_guides_operations';
import MealDetailNutrition from './meal_detail_nutrition';
import { useGetUserProfileQuery } from '../../app/smorgAppSyncGraphQLApiCustomHooks';

const MealDetailEditableMobile = ({
  menu,
  meal,
  savingInProgress,
  alwaysShowNutrition,
  showNutritionMetrics,
  showMacroRatio,
  derivedNutrition,
  canScaleIngredients,
  scaleToServings,
  currentlyEditingInline,
  ingredientSuggestions,
  ingredientSuggestionsNetworkState,
  onInlineEditFocus,
  onInlineEditChange,
  onInlineEditBlur,
  onAddArraySectionItem,
  onRemoveArraySectionItem,
  onIngredientCheckChange,
  onEditScaleToServings,
  onChangeScaleToServings,
  resetScaleToServings,
  onImageGeneratedAI,
  onNewImageChosen,
  onAddonChange,
  onTagsChange,
  onDialogClose,
  onUpdateIngredientStructuredData,
  initialIngredientsEditMode,
  hideFieldsForLoggingFoodItem,
}) => {
  const { data: userProfile } = useGetUserProfileQuery();

  const isSmorgStudio = userIsCreator(userProfile);

  const showPublisher = isSmorgStudio;

  const recentlyUsedTags = userProfile?.recentlyUsedTags || [];

  const { data: globalTagsSorted } = useGetGlobalTagsQuery(undefined);

  const tagsSorted = deduplicate([
    ...(recentlyUsedTags || []),
    ...(globalTagsSorted || []),
  ]);

  const recipe = meal && meal.recipes[0];

  const addonsEnabled = (meal.addons || []).map(
    (addonConfig) => addonConfig.name,
  );

  const nutritionEnabled =
    alwaysShowNutrition || addonsEnabled.includes(SMORG_NUTRITION_ADDON);

  const shouldShowMealDetailGuide = userProfile?.showMealDetailGuide;

  // const {
  //   isSupported: isWakeLockSupported,
  //   request: requestWakeLock,
  //   release: releaseWakeLock,
  // } = useWakeLock();

  // useEffect(() => {
  //   console.log({ isWakeLockSupported });
  //   requestWakeLock();
  //   return () => {
  //     console.log('Releasing wake lock');
  //     releaseWakeLock();
  //   };
  // }, []);

  const [updateUserProfile] = useUpdateUserProfileMutation();

  useEffect(() => {
    if (shouldShowMealDetailGuide) {
      showMealDetailGuide();
      updateUserProfile({ ...userProfile, showMealDetailGuide: false });
    }
  }, [shouldShowMealDetailGuide, updateUserProfile, userProfile]);

  const [shortDescriptionEditMode, setShortDescriptionEditMode] =
    useState(false);

  const [ingredientsEditMode, setIngredientsEditMode] = useState(
    initialIngredientsEditMode || false,
  );

  const onClickAddIngredient = () => {
    setIngredientsEditMode(true);
    onAddArraySectionItem('ingredients');
  };

  const onClickAddPreparation = () => {
    onAddArraySectionItem('preparations');
  };

  return (
    <DialogContent style={{ padding: 8 }}>
      <Grid container>
        <Grid item xs={12}>
          {!hideFieldsForLoggingFoodItem && (
            <Grid container>
              <Grid item xs={6}>
                <IconButton aria-label="back" onClick={onDialogClose}>
                  <ArrowBackIosOutlinedIcon />
                </IconButton>
              </Grid>
              <Grid item xs={6}>
                <Box display="flex" justifyContent="flex-end">
                  <MealDetailMobileActionsMenu
                    addonsEnabled={addonsEnabled}
                    onAddonChange={onAddonChange}
                  />
                </Box>
              </Grid>
            </Grid>
          )}
        </Grid>
        {!hideFieldsForLoggingFoodItem && (
          <Grid
            item
            xs={12}
            className="mealDetailImage"
            style={{
              position: 'relative',
              backgroundImage: `url("${
                meal.recipes[0].imageUrl || imgPlaceholder
              }")`,
            }}>
            <EditMealImagePopupMenu
              defaultAIPrompt={recipe?.title}
              style={{ position: 'absolute', bottom: 10, right: 10 }}
              onImageGeneratedAI={onImageGeneratedAI}
              onImageSelectedForUpload={onNewImageChosen}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Typography variant="h2" style={{ lineHeight: 1.4, fontSize: 28 }}>
            <EditableLabelAndInput
              edit={currentlyEditingInline.section === 'title'}
              defaultValue={recipe.title}
              labelPlaceholder="Set title"
              disabled={savingInProgress}
              inputStyle={{
                width: '100%',
              }}
              onFocus={() => onInlineEditFocus('title', null)}
              onChange={(value) => onInlineEditChange('title', null, value)}
              onDismiss={(reason) => onInlineEditBlur('title', null, reason)}
            />
          </Typography>
        </Grid>
        <Grid item xs={12}>
          <Grid container className="mealSubtitleGrid">
            <Grid item xs={12} style={{ fontSize: 14 }}>
              <Box className="mealSubtitleGridLabel">
                {menu?.title && <>in menu {menu.title}</>}
              </Box>
            </Grid>
            {showPublisher && (
              <Grid item xs={12} style={{ fontSize: 14, fontWeight: 'bold' }}>
                <Box className="mealSubtitleGridLabel">
                  {isURL(recipe.recipeUrl) && (
                    <a href={recipe.recipeUrl} target="_blank" rel="noreferrer">
                      {recipe.publisher ||
                        urlDisplayableHostName(recipe.recipeUrl)}
                    </a>
                  )}
                  {!isURL(recipe.recipeUrl) &&
                    (recipe.publisher ||
                      urlDisplayableHostName(recipe.recipeUrl))}
                </Box>
              </Grid>
            )}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Grid
            container
            direction="column"
            className={classNames('mealInfoGrid', {
              mealInfoGridLoggingFoodItem: hideFieldsForLoggingFoodItem,
            })}>
            <Grid item xs={6} className="mealInfoGridLabel">
              <h5>MEAL TYPE</h5>
            </Grid>
            <Grid item xs={6} className="mealInfoGridLabel">
              <h5>SERVINGS</h5>
            </Grid>
            {!hideFieldsForLoggingFoodItem && (
              <>
                <Grid item xs={6} className="mealInfoGridLabel">
                  <h5>PREP TIME (mins)</h5>
                </Grid>
                <Grid item xs={6} className="mealInfoGridLabel">
                  <h5>COOK TIME (mins)</h5>
                </Grid>
              </>
            )}
            <Grid item xs={6} style={{ height: 34 }}>
              <MealTypesInput
                edit={currentlyEditingInline.section === 'mealTypes'}
                defaultValues={recipe.mealTypes}
                placeholder="Set meal type"
                onFocus={() => onInlineEditFocus('mealTypes', null)}
                onChange={(values) =>
                  onInlineEditChange('mealTypes', null, values)
                }
                onDismiss={() => onInlineEditBlur('mealTypes', null)}
              />
            </Grid>
            <Grid item xs={6}>
              <div style={{ height: 35, display: 'flex' }}>
                <EditableLabelAndInput
                  edit={currentlyEditingInline.section === 'servings'}
                  defaultValue={recipe.servings}
                  labelPlaceholder="Set servings"
                  disabled={savingInProgress}
                  textStyle={{
                    margin: 'auto 0',
                    color: 'var(--space-primary-color)',
                    fontWeight: 'bold',
                  }}
                  onFocus={() => onInlineEditFocus('servings', null)}
                  onChange={(value) =>
                    onInlineEditChange('servings', null, value)
                  }
                  onDismiss={(reason) =>
                    onInlineEditBlur('servings', null, reason)
                  }
                />
              </div>
            </Grid>
            {!hideFieldsForLoggingFoodItem && (
              <>
                <Grid item xs={6}>
                  <div style={{ height: 35, display: 'flex' }}>
                    <EditableLabelAndInput
                      edit={currentlyEditingInline.section === 'prepTime'}
                      defaultValue={recipe.prepTime}
                      labelPlaceholder="Set prep time"
                      disabled={savingInProgress}
                      textStyle={{
                        margin: 'auto 0',
                        color: 'var(--space-primary-color)',
                        fontWeight: 'bold',
                      }}
                      onFocus={() => onInlineEditFocus('prepTime', null)}
                      onChange={(value) =>
                        onInlineEditChange('prepTime', null, value)
                      }
                      onDismiss={(reason) =>
                        onInlineEditBlur('prepTime', null, reason)
                      }
                    />
                  </div>
                </Grid>
                <Grid item xs={6} className="contenteditable">
                  <div style={{ height: 35, display: 'flex' }}>
                    <EditableLabelAndInput
                      edit={currentlyEditingInline.section === 'cookTime'}
                      defaultValue={recipe.cookTime}
                      labelPlaceholder="Set cook time"
                      disabled={savingInProgress}
                      textStyle={{
                        margin: 'auto 0',
                        color: 'var(--space-primary-color)',
                        fontWeight: 'bold',
                      }}
                      onFocus={() => onInlineEditFocus('cookTime', null)}
                      onChange={(value) =>
                        onInlineEditChange('cookTime', null, value)
                      }
                      onDismiss={(reason) =>
                        onInlineEditBlur('cookTime', null, reason)
                      }
                    />
                  </div>
                </Grid>
              </>
            )}
          </Grid>
          {!hideFieldsForLoggingFoodItem && (
            <Grid container>
              <Grid item xs={12}>
                <h5 style={{ marginTop: '10px', marginBottom: 0 }}>CHEF</h5>
              </Grid>
              <Grid item xs={12}>
                <EditableLabelAndInput
                  edit={currentlyEditingInline.section === 'chef'}
                  defaultValue={recipe.chef}
                  labelPlaceholder="Add chef info"
                  disabled={savingInProgress}
                  textStyle={{
                    width: '100%',
                    margin: 'auto 0',
                    color: 'var(--app-primary-color)',
                  }}
                  inputStyle={{ width: '100%' }}
                  onFocus={() => onInlineEditFocus('chef', null)}
                  onChange={(value) => onInlineEditChange('chef', null, value)}
                  onDismiss={(reason) => onInlineEditBlur('chef', null, reason)}
                />
              </Grid>
              <Grid item xs={12}>
                <h5 style={{ marginTop: '10px', marginBottom: 0 }}>
                  CATEGORY TAGS
                </h5>
              </Grid>
              <Grid item xs={12}>
                <TagsInput
                  tags={recipe.tags || []}
                  tagsSorted={tagsSorted}
                  onTagsChange={onTagsChange}
                  placeholder="Add a tag and press Enter"
                />
              </Grid>
            </Grid>
          )}
        </Grid>
        {!hideFieldsForLoggingFoodItem && (
          <>
            <Grid item xs={12}>
              <Typography variant="h3">
                Description
                <div className="ingredientsEditModeWidget">
                  {shortDescriptionEditMode ? (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      href="#"
                      onClick={(ev) => {
                        setShortDescriptionEditMode(false);
                        ev.preventDefault();
                      }}>
                      Close
                      <CloseIcon />
                    </a>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      href="#"
                      onClick={(ev) => {
                        setShortDescriptionEditMode(true);
                        ev.preventDefault();
                      }}>
                      Edit
                      <EditIcon />
                    </a>
                  )}
                </div>
              </Typography>
            </Grid>
            <Grid item xs={12}>
              <EditableLabelAndInput
                multiline
                textStyle={{
                  width: '100%',
                  marginBottom: '16px',
                }}
                inputStyle={{ width: '100%', marginBottom: '16px' }}
                edit={shortDescriptionEditMode}
                defaultValue={recipe.shortDescription}
                labelPlaceholder="Click to add a description"
                disabled={savingInProgress}
                onFocus={() => onInlineEditFocus('shortDescription', null)}
                onChange={(value) =>
                  onInlineEditChange('shortDescription', null, value)
                }
                onDismiss={(reason) =>
                  onInlineEditBlur('shortDescription', null, reason)
                }
              />
            </Grid>
          </>
        )}
        {nutritionEnabled && !hideFieldsForLoggingFoodItem && (
          <Grid item xs={12}>
            <MealDetailNutrition
              derivedNutrition={derivedNutrition}
              showNutritionMetrics={showNutritionMetrics}
              showMacroRatio={showMacroRatio}
            />
          </Grid>
        )}
        <Grid item xs={12}>
          <Grid container spacing={2}>
            <Grid item xs={9}>
              <Typography variant="h3">
                Ingredients
                <div className="ingredientsEditModeWidget">
                  {ingredientsEditMode ? (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      href="#"
                      onClick={(ev) => {
                        setIngredientsEditMode(false);
                        ev.preventDefault();
                      }}>
                      Close
                      <CloseIcon />
                    </a>
                  ) : (
                    // eslint-disable-next-line jsx-a11y/anchor-is-valid
                    <a
                      href="#"
                      onClick={(ev) => {
                        setIngredientsEditMode(true);
                        ev.preventDefault();
                      }}>
                      Edit
                      <EditIcon />
                    </a>
                  )}
                </div>
              </Typography>
            </Grid>
            <Grid item xs={3} style={{ paddingTop: '40px', textAlign: 'left' }}>
              &nbsp;
            </Grid>
            <Grid item xs={9} style={{ paddingTop: 0 }}>
              {canScaleIngredients && (
                <span className="scaledForWidget">
                  scaled for
                  <ServingsInput
                    value={scaleToServings}
                    disabled={!ingredientsEditMode || savingInProgress}
                    onChange={onEditScaleToServings}
                    onSave={onChangeScaleToServings}
                    onReset={resetScaleToServings}
                  />
                </span>
              )}
            </Grid>
            <Grid item xs={12}>
              <IngredientListMobile
                meal={meal}
                savingInProgress={savingInProgress}
                ingredientSuggestions={ingredientSuggestions}
                ingredientSuggestionsNetworkState={
                  ingredientSuggestionsNetworkState
                }
                ingredientNutrition={
                  derivedNutrition?.ingredientNutrition || []
                }
                onRemoveItem={(id) =>
                  onRemoveArraySectionItem('ingredients', id)
                }
                onAddItem={onClickAddIngredient}
                currentlyEditingInline={currentlyEditingInline}
                onInlineEditFocus={onInlineEditFocus}
                onInlineEditChange={onInlineEditChange}
                onInlineEditBlur={onInlineEditBlur}
                editMode={ingredientsEditMode}
                onIngredientCheckChange={onIngredientCheckChange}
                onUpdateIngredientStructuredData={
                  onUpdateIngredientStructuredData
                }
              />
            </Grid>
            {!hideFieldsForLoggingFoodItem && (
              <>
                <Grid item xs={9}>
                  <Typography variant="h3">Methods</Typography>
                </Grid>
                <Grid item xs={3}>
                  &nbsp;
                </Grid>
                <Grid item xs={12}>
                  <PreparationList
                    meal={meal}
                    savingInProgress={savingInProgress}
                    onRemoveItem={(id) =>
                      onRemoveArraySectionItem('preparations', id)
                    }
                    onAddItem={onClickAddPreparation}
                    currentlyEditingInline={currentlyEditingInline}
                    onInlineEditFocus={onInlineEditFocus}
                    onInlineEditChange={onInlineEditChange}
                    onInlineEditBlur={onInlineEditBlur}
                  />
                </Grid>
              </>
            )}
          </Grid>
          {!hideFieldsForLoggingFoodItem && (
            <>
              <Grid item xs={9}>
                <Typography variant="h3">Notes</Typography>
              </Grid>
              <Grid item xs={3}>
                &nbsp;
              </Grid>
              <Grid item xs={12}>
                <EditableLabelAndInput
                  multiline
                  readOnly={false}
                  textStyle={{
                    width: '100%',
                  }}
                  inputStyle={{ width: '100%' }}
                  edit={currentlyEditingInline.section === 'recipeNotes'}
                  defaultValue={recipe?.recipeNotes}
                  labelPlaceholder="Click to add notes"
                  disabled={false}
                  onFocus={() => onInlineEditFocus('recipeNotes', null)}
                  onChange={(value) =>
                    onInlineEditChange('recipeNotes', null, value)
                  }
                  onDismiss={(reason) =>
                    onInlineEditBlur('recipeNotes', null, reason)
                  }
                />
              </Grid>
            </>
          )}
          <Grid item xs={12} style={{ height: '40px' }}>
            &nbsp;
          </Grid>
        </Grid>
        {nutritionEnabled && hideFieldsForLoggingFoodItem && (
          /* When logging a food item, show nutrition at the bottom */
          <Grid item xs={12}>
            <MealDetailNutrition
              derivedNutrition={derivedNutrition}
              showNutritionMetrics={showNutritionMetrics}
              showMacroRatio={showMacroRatio}
            />
          </Grid>
        )}
        <Grid item xs={12} style={{ height: '40px' }}>
          &nbsp;
        </Grid>
      </Grid>
    </DialogContent>
  );
};

MealDetailEditableMobile.propTypes = {
  menu: PropTypes.shape({
    title: PropTypes.string.isRequired,
  }).isRequired,
  meal: PropTypes.shape({
    id: PropTypes.string.isRequired,
    recipes: PropTypes.arrayOf(
      PropTypes.shape({
        title: PropTypes.string,
        shortDescription: PropTypes.string,
        imageUrl: PropTypes.string,
        recipeUrl: PropTypes.string,
        mealiqId: PropTypes.string,
        mealTypes: PropTypes.arrayOf(PropTypes.string),
        nutrition: PropTypes.shape({
          kCal: PropTypes.number,
          protein: PropTypes.number,
          fat: PropTypes.number,
          carbohydrate: PropTypes.number,
          fibre: PropTypes.number,
          sugar: PropTypes.number,
        }),
        supportedDiets: PropTypes.arrayOf(PropTypes.string),
        ingredients: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            fullText: PropTypes.string,
          }),
        ),
        preparations: PropTypes.arrayOf(
          PropTypes.shape({
            id: PropTypes.string,
            fullText: PropTypes.string,
          }),
        ),
        utensils: PropTypes.arrayOf(PropTypes.string).isRequired,
        servings: PropTypes.number,
        cookTime: PropTypes.number,
        prepTime: PropTypes.number,
        totalTime: PropTypes.number,
      }),
    ).isRequired,
    addons: PropTypes.arrayOf(
      PropTypes.shape({
        name: PropTypes.string.isRequired,
      }),
    ),
  }).isRequired,
  savingInProgress: PropTypes.bool.isRequired,
  alwaysShowNutrition: PropTypes.bool,
  derivedNutrition: PropTypes.shape({
    totalNutritionPerServing: PropTypes.objectOf(PropTypes.number),
    // eslint-disable-next-line react/forbid-prop-types
    ingredientNutrition: PropTypes.arrayOf(PropTypes.object).isRequired,
    macroRatio: PropTypes.objectOf(PropTypes.number).isRequired,
    foodAllergens: PropTypes.arrayOf(PropTypes.string).isRequired,
    foodPreferences: PropTypes.arrayOf(PropTypes.string).isRequired,
    status: PropTypes.bool.isRequired,
  }).isRequired,
  showNutritionMetrics: PropTypes.arrayOf(PropTypes.string).isRequired,
  showMacroRatio: PropTypes.bool.isRequired,
  canScaleIngredients: PropTypes.bool.isRequired,
  scaleToServings: PropTypes.number.isRequired,
  currentlyEditingInline: PropTypes.shape({
    section: PropTypes.string.isRequired,
    itemId: PropTypes.string,
  }).isRequired,
  ingredientSuggestions: PropTypes.arrayOf(
    PropTypes.shape({
      suggestionText: PropTypes.string.isRequired,
    }),
  ).isRequired,
  ingredientSuggestionsNetworkState: PropTypes.shape({
    loading: PropTypes.bool,
  }).isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  onInlineEditFocus: PropTypes.func.isRequired,
  onInlineEditChange: PropTypes.func.isRequired,
  onInlineEditBlur: PropTypes.func.isRequired,
  onAddArraySectionItem: PropTypes.func.isRequired,
  onRemoveArraySectionItem: PropTypes.func.isRequired,
  onIngredientCheckChange: PropTypes.func.isRequired,
  onEditScaleToServings: PropTypes.func.isRequired,
  onChangeScaleToServings: PropTypes.func.isRequired,
  resetScaleToServings: PropTypes.func.isRequired,
  onImageGeneratedAI: PropTypes.func.isRequired,
  onNewImageChosen: PropTypes.func.isRequired,
  onAddonChange: PropTypes.func.isRequired,
  onTagsChange: PropTypes.func.isRequired,
  onDialogClose: PropTypes.func.isRequired,
  onUpdateIngredientStructuredData: PropTypes.func.isRequired,
  initialIngredientsEditMode: PropTypes.bool,
  hideFieldsForLoggingFoodItem: PropTypes.bool,
};

MealDetailEditableMobile.defaultProps = {
  alwaysShowNutrition: false,
  initialIngredientsEditMode: false,
  hideFieldsForLoggingFoodItem: false,
};

export default MealDetailEditableMobile;
